/**
 * API client — fetch wrapper with JWT auth injection.
 *
 * - `apiFetch`       — authenticated requests (injects Bearer token)
 * - `apiPublicFetch` — unauthenticated requests (login, signup, OAuth)
 * - `apiFetchRaw`    — authenticated, returns raw Response (for blobs / SSE)
 */

import config from "../config";

export const API_BASE = `${config.apiBasePath}/api/v1`;
const TOKEN_KEY = "token";

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

/** Read and validate the stored JWT. Returns null if missing or expired. */
export function getValidAccessToken(): string | null {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
        const parts = token.split(".");
        const payloadPart = parts[1];
        if (!payloadPart) return null;
        const payload = JSON.parse(atob(payloadPart));
        return payload.exp * 1000 > Date.now() ? token : null;
    } catch {
        return null;
    }
}

export function setAccessToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
}

export function clearAccessToken(): void {
    localStorage.removeItem(TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export class ApiError extends Error {
    constructor(
        public status: number,
        public statusText: string,
        public body?: unknown,
    ) {
        super(`API ${status}: ${statusText}`);
        this.name = "ApiError";
    }
}

// ---------------------------------------------------------------------------
// URL builder (handles query params like axios `params`)
// ---------------------------------------------------------------------------

function buildUrl(
    path: string,
    params?: Record<string, unknown>,
): string {
    let url = `${API_BASE}${path}`;
    if (params) {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) continue;
            if (Array.isArray(value)) {
                value.forEach((v) => sp.append(key, String(v)));
            } else {
                sp.set(key, String(value));
            }
        }
        const qs = sp.toString();
        if (qs) url += `?${qs}`;
    }
    return url;
}

// ---------------------------------------------------------------------------
// Fetch options
// ---------------------------------------------------------------------------

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
    params?: Record<string, unknown>;
    body?: unknown;
}

// ---------------------------------------------------------------------------
// Internal base
// ---------------------------------------------------------------------------

async function baseFetch<T>(
    path: string,
    options?: ApiFetchOptions,
    authToken?: string | null,
): Promise<T> {
    const {params, body, headers: customHeaders, ...restInit} = options ?? {};

    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(customHeaders as Record<string, string>),
    };

    if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
    }

    let processedBody: BodyInit | undefined;
    if (body !== undefined) {
        if (
            body instanceof FormData ||
            body instanceof URLSearchParams ||
            typeof body === "string"
        ) {
            processedBody = body;
        } else {
            processedBody = JSON.stringify(body);
        }
    }

    const url = buildUrl(path, params);
    const res = await fetch(url, {...restInit, headers, body: processedBody});

    // Clear stale auth on 401 (matches old axios interceptor behaviour)
    if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
    }

    if (!res.ok) {
        const responseBody = await res.json().catch(() => undefined);
        throw new ApiError(res.status, res.statusText, responseBody);
    }

    // 204 No Content or empty body — return undefined instead of parsing
    if (res.status === 204 || res.headers.get('content-length') === '0') {
        return undefined as T;
    }
    return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Authenticated request — auto-injects the stored JWT. */
export async function apiFetch<T>(
    path: string,
    options?: ApiFetchOptions,
): Promise<T> {
    const token = getValidAccessToken();
    if (!token) {
        throw new ApiError(401, "No valid access token");
    }
    return baseFetch<T>(path, options, token);
}

/** Public request — no auth header (login, signup, OAuth). */
export async function apiPublicFetch<T>(
    path: string,
    options?: ApiFetchOptions,
): Promise<T> {
    return baseFetch<T>(path, options);
}

/**
 * Authenticated raw fetch — returns the Response directly.
 * Use for blobs, SSE streams, or any non-JSON response.
 */
export async function apiFetchRaw(
    path: string,
    options?: ApiFetchOptions,
): Promise<Response> {
    const token = getValidAccessToken();
    if (!token) {
        throw new ApiError(401, "No valid access token");
    }

    const {params, body, headers: customHeaders, ...restInit} = options ?? {};

    const headers: Record<string, string> = {
        ...(customHeaders as Record<string, string>),
    };
    headers["Authorization"] = `Bearer ${token}`;

    let processedBody: BodyInit | undefined;
    if (body !== undefined) {
        if (
            body instanceof FormData ||
            body instanceof URLSearchParams ||
            typeof body === "string"
        ) {
            processedBody = body;
        } else {
            processedBody = JSON.stringify(body);
            if (!headers["Content-Type"]) {
                headers["Content-Type"] = "application/json";
            }
        }
    }

    const url = buildUrl(path, params);
    const res = await fetch(url, {...restInit, headers, body: processedBody});

    if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
    }

    if (!res.ok) {
        const responseBody = await res.text().catch(() => undefined);
        throw new ApiError(res.status, res.statusText, responseBody);
    }

    return res;
}
